import axios from '../../../common/utils/interceptor';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import React, { useEffect, useState } from 'react';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { apiUrls } from '../../../constants/apiUrls';
import { AuthPageStyles } from '../../styles';
import { FullPageLoader } from '../../../common/components';
import { GAPageViewMappings, GARouteMappings } from '../../../constants/gaMappings';
import { getCookie, setCookie } from '../../../common/utils/cookies';
import { getLocalizedKeyByErrorMessage } from '../../utils/errorMessage';
import { MfsLoginFields, MfsSignUpRequest } from '../../interfaces/AuthInputFields';
import { MfsLoginForm } from './MfsLoginForm';
import { MfsLoginMode } from '../../../constants/mfsLoginModes';
import { MfsPasswordExpiredForm } from './MfsPasswordExpiredForm';
import { routeMappings } from '../../../constants/routeMappings';
import { sendPageViewDetails, sendSHL360LoginDetails } from '../../../common/utils/gaEvents';
import { updateSessionStorageForLoggedinUser } from '../../utils/updateSessionStorage';
import { useBasicDetails } from '../../../store/storeApi';
import { useHistory } from 'react-router-dom';
import { useTheme } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import {
  matchPassword,
  validateInputChangeMfsSignUpField,
  validateDropdownField,
  validateTextField,
  getPasswordStrength,
  getPasswordStrengthMessageList,
} from '../../utils/validation';
import { reValidateSession } from '../../utils/revalidateSession';
import { redirectUrlOnAuthentication } from '../../../common/utils/redirectUrlOnAuthentication';
import { SessionStore } from '../../../constants';

export type MfsLoginModeType = 'SIGNIN' | 'SIGNUP' | 'FORGOT_RESET' | 'PASSWORD_EXPIRED' | 'USER_SUSPENDED';
const ACCOUNT_LOCKED_ATTEMPTS = 4;

/** Container Component for MFS Login Page */
export const MfsLoginPage = (): JSX.Element => {
  const theme = useTheme();
  const {
    template: { settings },
    headerDetails,
    updateHeaderDetails,
  } = useBasicDetails();

  const lgUp = useMediaQuery(theme.breakpoints.up('lg'));
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const classes = AuthPageStyles(settings);

  const [loginFields, setLoginFields] = useState<MfsLoginFields>({
    name: '',
    username: '',
    email: '',
    languages: [],
    language: '',
    nameHelperText: '',
    usernameHelperText: '',
    emailHelperText: '',
    password: '',
    passwordHelperText: '',
    confirmPassword: '',
    confirmPasswordHelperText: '',
    languageHelperText: '',
    newPassword: '',
    newPasswordHelperText: '',
    confirmNewPassword: '',
    confirmNewPasswordHelperText: '',
  });
  const [loginFieldsLoaded, setLoginFieldsLoaded] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingOnLogin, setLoadingOnLogin] = useState<boolean>(false);
  const [loginMode, setLoginMode] = useState<MfsLoginModeType>();
  const [username, setUsername] = useState<string | undefined>(undefined);
  const [userSuspended, setUserSuspended] = useState<boolean>(false);
  const [pageStartTime] = useState<Date>(new Date());

  /* Page View GA method */
  const loginPageViews = (loginMode: string): void => {
    let pathName = GARouteMappings.SHL360_LOGIN;
    let pageName = GAPageViewMappings.SHL360_LOGIN;
    switch (loginMode) {
      case MfsLoginMode.SIGNUP:
        pathName = GARouteMappings.SHL360_REGISTER;
        pageName = GARouteMappings.SHL360_REGISTER;
        break;
      case MfsLoginMode.FORGOT_RESET:
        pathName = GARouteMappings.SHL360_FORGOT_PASSWORD;
        pageName = GARouteMappings.SHL360_FORGOT_PASSWORD;
        break;
      case MfsLoginMode.PASSWORD_EXPIRED:
        pathName = GARouteMappings.SHL360_RESET_PASSWORD;
        pageName = GARouteMappings.SHL360_RESET_PASSWORD;
        break;
    }
    sendPageViewDetails(pathName, pageName);
  };

  /** Fetching custom sign up fields for handling login tabs and fields */
  useEffect(() => {
    const fetchLoginFields = async (): Promise<void> => {
      setLoading(true);
      try {
        const { data } = await axios.get(apiUrls.GET_MFS_LOGIN_DETAILS);
        if (data) {
          const tmpLoginFields = { ...loginFields };
          let mode = data.userJourney as MfsLoginModeType;
          const isUserSuspended = mode === MfsLoginMode.USER_SUSPENDED;

          if (isUserSuspended) {
            setUserSuspended(isUserSuspended);
            mode = MfsLoginMode.SIGNIN;
          }
          const isModeSignIn = mode === MfsLoginMode.SIGNIN;

          setLoginFields({
            ...tmpLoginFields,
            name: `${data.firstName} ${data.lastName}`,
            username: isModeSignIn ? '' : data.username,
            email: data.email,
            languages: data.languageList,
            language: data.selectedLanguage,
          });
          setLoginMode(isUserSuspended ? MfsLoginMode.SIGNIN : mode);
          loginPageViews(isUserSuspended ? MfsLoginMode.SIGNIN : mode);

          if (isModeSignIn) {
            setUsername(data.username);
          }
        }
      } catch (e) {
        setErrorMessage('genericError');
      } finally {
        setLoading(false);
      }
    };

    if (!loginFieldsLoaded) {
      setLoginFieldsLoaded(true);
      fetchLoginFields();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** input field change/Blur Event handler */
  const onInputChange = (name: keyof MfsLoginFields) => (event: React.ChangeEvent<{ value: string }>): void => {
    const value = event.target.value;

    if (name === 'language') {
      const lang = value.toUpperCase();
      setCookie('locale_player', lang);
      setCookie('oldlang', lang);
      updateHeaderDetails({ ...headerDetails, language: lang });
      i18n.changeLanguage(lang.toLowerCase());
    }
    const valueWithValidation = validateInputChangeMfsSignUpField(name, value, loginFields);

    setLoginFields({ ...loginFields, ...valueWithValidation });
  };

  const processErrorMessage = (msg: string): void => {
    // navigate to forget password page in case account is locked
    if (msg === 'signin.user.locked') {
      history.push(`${routeMappings['MFS']}${routeMappings['ACCOUNT_LOCKED']}`);
    } else if (msg === 'signpage.invalid.emailpassword' && loginMode === MfsLoginMode.SIGNIN) {
      const usernamePasswordInvalid = `
        <div>${t('usernamePasswordInvalid')}</div>
        <br/>
        <div>${t('accountLockedInfo', { counter: ACCOUNT_LOCKED_ATTEMPTS })}</div>`;
      setErrorMessage(usernamePasswordInvalid);
    } else {
      setErrorMessage(t(getLocalizedKeyByErrorMessage(msg)));
    }
  };

  /** Sign in button click handler */
  const loginCandidate = async (): Promise<void> => {
    let loader = false;
    try {
      setLoadingOnLogin(true);
      setErrorMessage('');
      const request: Partial<MfsSignUpRequest> = {
        actionType: loginMode === MfsLoginMode.SIGNIN ? 'SIGN_IN' : 'SIGN_UP',
        locale: loginFields.language || getCookie('locale_player') || 'en_US',
        password: loginFields.password,
        confirmPassword: loginFields.confirmPassword,
        email: loginFields.email,
        username: loginFields.username,
        mfsEnabled: true,
      };

      if (loginMode === MfsLoginMode.PASSWORD_EXPIRED) {
        request.password = loginFields.newPassword;
        request.confirmPassword = loginFields.confirmNewPassword;
        request.oldPassword = loginFields.password;
      }

      const { data } = await axios.post(apiUrls.LOGIN, request);

      if (data.IsError) {
        processErrorMessage(data.Message);
      } else {
        /** get candidate details from API data */
        let candidate, candidateDeadlineDate;
        if (data.Data && data.Data.candidate) {
          candidate = JSON.stringify(data.Data.candidate);
        }
        if (data.Data && data.Data.candidateDeadlineDate) {
          candidateDeadlineDate = data.Data.candidateDeadlineDate;
        }

        /** update sessionStorage */
        updateSessionStorageForLoggedinUser(candidate, candidateDeadlineDate);

        loader = true;
        history.push(`${routeMappings['MFS']}${routeMappings['ACCOUNT']}`);
      }
    } catch (e) {
      if (e && e.data && e.data.Message) {
        processErrorMessage(e.data.Message);
      }
    } finally {
      if (!loader) {
        // Done in order to not throw react error
        setLoadingOnLogin(false);
      }
    }
  };

  /** Sign in button click handler */
  const onLoginClick = async (event: React.SyntheticEvent): Promise<void> => {
    event.preventDefault();
    const usernameHelperValues = validateTextField('username', loginFields.username);
    const passwordHelperValues = validateTextField('password', loginFields.password);
    const confirmPasswordHelperValues = validateTextField('confirmPassword', loginFields.confirmPassword);
    const langaugeHelperValues = validateDropdownField('language', loginFields.language);
    const newPasswordHelperValues = validateTextField('newPassword', loginFields.newPassword);
    const confirmNewPasswordHelperValues = validateTextField('confirmNewPassword', loginFields.confirmNewPassword);

    const isModeSignupOrReset = loginMode === MfsLoginMode.SIGNUP || loginMode === MfsLoginMode.FORGOT_RESET;
    /** if confirm password does not match the password for signup and reset */
    if (isModeSignupOrReset && confirmPasswordHelperValues.confirmPasswordHelperText === '') {
      confirmPasswordHelperValues.confirmPasswordHelperText = matchPassword(
        loginFields.password,
        loginFields.confirmPassword,
      );
    }

    /** if confirm password does not match the password for password expiry */
    if (
      loginMode === MfsLoginMode.PASSWORD_EXPIRED &&
      confirmNewPasswordHelperValues.confirmNewPasswordHelperText === ''
    ) {
      confirmNewPasswordHelperValues.confirmNewPasswordHelperText = matchPassword(
        loginFields.newPassword,
        loginFields.confirmNewPassword,
      );
    }

    if (
      passwordHelperValues.passwordHelperText !== '' ||
      (loginMode === MfsLoginMode.SIGNIN && usernameHelperValues.usernameHelperText !== '') ||
      (isModeSignupOrReset && confirmPasswordHelperValues.confirmPasswordHelperText !== '') ||
      (loginMode === MfsLoginMode.SIGNUP && langaugeHelperValues.languageHelperText !== '') ||
      (loginMode === MfsLoginMode.PASSWORD_EXPIRED && newPasswordHelperValues.newPasswordHelperText !== '') ||
      (loginMode === MfsLoginMode.PASSWORD_EXPIRED &&
        confirmNewPasswordHelperValues.confirmNewPasswordHelperText !== '')
    ) {
      setLoginFields({
        ...loginFields,
        usernameHelperText: usernameHelperValues.usernameHelperText,
        passwordHelperText: passwordHelperValues.passwordHelperText,
        confirmPasswordHelperText: confirmPasswordHelperValues.confirmPasswordHelperText,
        languageHelperText: langaugeHelperValues.languageHelperText,
        newPasswordHelperText: newPasswordHelperValues.newPasswordHelperText,
        confirmNewPasswordHelperText: confirmNewPasswordHelperValues.confirmNewPasswordHelperText,
      });
      return;
    }

    if (loginMode === MfsLoginMode.SIGNIN && username !== loginFields.username) {
      setErrorMessage(t('usernamePasswordInvalid'));
      return;
    }

    if (
      isModeSignupOrReset &&
      loginFields.password &&
      loginFields.email &&
      loginFields.password.toLowerCase() === loginFields.email.toLowerCase()
    ) {
      setErrorMessage(t('emailSameAsPasswordMsg'));
      return;
    }
    if (
      loginMode === MfsLoginMode.PASSWORD_EXPIRED &&
      loginFields.newPassword &&
      loginFields.email &&
      loginFields.newPassword.toLowerCase() === loginFields.email.toLowerCase()
    ) {
      setErrorMessage(t('emailSameAsPasswordMsg'));
      return;
    }

    if (userSuspended) {
      setErrorMessage(t('userSuspendedMessage'));
      return;
    }

    // Password check for validations
    if (
      (isModeSignupOrReset && passwordHelperValues.value !== '') ||
      (loginMode === MfsLoginMode.PASSWORD_EXPIRED && newPasswordHelperValues.value !== '')
    ) {
      const pswdText = loginMode === MfsLoginMode.PASSWORD_EXPIRED ? loginFields.newPassword : loginFields.password;
      const passwordStrength = getPasswordStrength(pswdText);
      const passwordStrings = getPasswordStrengthMessageList(passwordStrength);
      if (passwordStrings.length > 0) {
        return;
      }
    }
    // GA
    const gaLoginCategory = loginMode === MfsLoginMode.SIGNUP ? 'LINK' : 'Generic';
    sendSHL360LoginDetails(gaLoginCategory, pageStartTime);
    // Login candidate
    loginCandidate();
  };

  return (
    <>
      <FullPageLoader isLoading={loading || loginMode === undefined} />

      {loginMode && (
        <Grid container justify="center" className={classes.container}>
          <Grid item xl={8} lg={10} className={classes.grid}>
            <Paper elevation={lgUp ? 1 : 0} className={classes.paper}>
              {loginMode === MfsLoginMode.PASSWORD_EXPIRED ? (
                <MfsPasswordExpiredForm
                  formFields={loginFields}
                  loadingOnLogin={loadingOnLogin}
                  onLoginClick={onLoginClick}
                  onInputChange={onInputChange}
                  errorMessage={errorMessage}
                />
              ) : (
                <MfsLoginForm
                  formFields={loginFields}
                  loadingOnLogin={loadingOnLogin}
                  onLoginClick={onLoginClick}
                  onInputChange={onInputChange}
                  errorMessage={errorMessage}
                  loginMode={loginMode}
                />
              )}
            </Paper>
          </Grid>
        </Grid>
      )}
    </>
  );
};
